import React from "react";

import {
  AccountBalanceRounded,
  BiotechRounded,
  DinnerDiningRounded,
  EggAltRounded,
  FunctionsRounded,
  InfoRounded,
  OpenInNewRounded,
  RefreshRounded,
  RiceBowlRounded,
  SoupKitchenRounded,
} from "@mui/icons-material";
import { Box, Chip, IconButton, Skeleton } from "@mui/material";
import { httpsCallable } from "firebase/functions";
import { DateTime } from "luxon";

import useAuth from "../../../../../../config/context/authContext";
import useFirebase from "../../../../../../config/context/firebaseContext";
import useModal from "../../../../../../config/context/modalContext";
import { MenuService } from "../../../../../../config/services/MenuService";
import { GreyPallet } from "../../../../../../config/styles";
import { Dev } from "../../../../../../config/util/consoleUtil";
import { UtilComponents } from "../../../../../../config/util/utilComponents";

import MainSolidButton from "../../../../../../components/MainSolidButton";
import MainText from "../../../../../../components/MainText";

import { MenuItem } from "../../../../../../types/database";

export default function RuPage() {
  const MenuListOne = MenuService.ListOne();

  const { functions, analyticLog } = useFirebase();

  const { currentUserState } = useAuth();

  const { pushAlert } = useModal();

  const [loading, setLoading] = React.useState(false);
  const [loadingForce, setLoadingForce] = React.useState(false);

  const [firstMenu, setFirstMenu] = React.useState<null | boolean>(false);
  const [secondMenu, setSecondMenu] = React.useState<null | boolean>(false);

  const [menu, setMenu] = React.useState<MenuItem>();

  const timestampDay = DateTime.now().startOf("day").toMillis();

  const todayString = DateTime.now().toFormat("y-LL-dd");

  const getMenu = async () => {
    try {
      setLoading(true);

      const result = (await MenuListOne.runRequest(timestampDay)) as MenuItem;

      const timeStampTwoHours = DateTime.now().minus({ hour: 1 }).toMillis();

      if (result?.dinner?.protein || result?.updatedAtTimestamp > timeStampTwoHours)
        setMenu(result as MenuItem);
      else await handleForceGetMenu();
    } catch (error) {
      console.log(...new Dev("getMenu", error).Error());
      pushAlert({ message: "Erro ao obter menu", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleForceGetMenu = async () => {
    try {
      setLoadingForce(true);

      const updateFoodMenu = httpsCallable(functions, "updateFoodMenu");

      const response = await updateFoodMenu({});

      console.log(...new Dev("handleForceGetMenu", response?.data).Log());

      if ((response?.data as any).menu) setMenu((response.data as any).menu);

      pushAlert({ type: "success", message: "Cardápio atualizado" });
    } catch (error) {
      console.log(...new Dev("handleForceGetMenu", error).Error());
      pushAlert({
        type: "error",
        message: "Erro ao atualizar cardápio. " + (error as Error).message,
      });
    } finally {
      setLoadingForce(false);
    }
  };

  const separateText = (text: string) => {
    // • ⁕ ↷ ∴ ⋆ ◉ ☆
    if (!text) return "Não cadastrado";

    const sepChar = "•";
    return text.split(",").map((item, index) =>
      index % 2 === 0 ? (
        <span key={index} style={{ color: "black" }}>
          {index === 0 ? null : <>&nbsp;&nbsp;{sepChar}&nbsp;</>}
          {item}
        </span>
      ) : (
        <span key={index}>
          {index === 0 ? null : <>&nbsp;&nbsp;{sepChar}&nbsp;</>}
          {item}
        </span>
      )
    );
  };

  const handleNotFoundInfoClick = () => {
    pushAlert({
      type: "default",
      title: "⛔ Erro",
      isClosable: true,
      children: (
        <>
          <Box display={"flex"} alignItems={"center"} mb={1}>
            <MainText size="body">Cardápio não encontrado</MainText>
          </Box>
          <Box mb={1}>
            <MainText size="caption" color="regular">
              Parece que o cardápio foi gerado com erros. Isso pode acontecer quando o site do RU
              está fora do ar.
            </MainText>
          </Box>
          <Box mb={1}>
            <MainText size="caption" color="regular">
              Aqui também tento exibir as postagens do Instagram do RU.
            </MainText>
          </Box>
          <Box mb={2}>
            <MainText size="caption" color="regular">
              Essa ferramenta não é oficial, então pode ter erros. Pra sanar dúvidas visite o
              Instagram oficial pra ter as informações atualizadas sobre o RU.
            </MainText>
          </Box>

          <Chip
            label={
              <Box display={"flex"} alignItems={"center"} style={{ color: GreyPallet.Regular }}>
                <OpenInNewRounded fontSize={"small"} sx={{ mr: 1 }} />
                <MainText size="small" color="regular">
                  @rudaufrn
                </MainText>
              </Box>
            }
            variant={"filled"}
            style={{ borderWidth: 3 }}
            onClick={() => window.open("https://www.instagram.com/stories/rudaufrn/")}
          />
        </>
      ),
    });
  };

  const handleLunchInfoClick = () => {
    analyticLog("click_ru_info", { meal: "lunch" });
    pushAlert({
      type: "default",
      title: "Horários do Almoço",
      isClosable: true,
      children: (
        <>
          <Box display={"flex"} alignItems={"center"} mb={1}>
            <AccountBalanceRounded />
            <MainText size="body">Unidade Central</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Segunda à sexta das 10h30 às 13h30.
          </MainText>
          <MainText size="caption" color="regular">
            - Sábados, domingos e feriados das 11h às 13h.
          </MainText>

          <Box display={"flex"} alignItems={"center"} mt={2} mb={1}>
            <BiotechRounded />
            <MainText size="body">Unidade Biomédica</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Todos os dias, das 11h às 13h.
          </MainText>

          <Box display={"flex"} alignItems={"center"} mt={2} mb={1}>
            <FunctionsRounded />
            <MainText size="body">Unidade Tecnológica</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Segunda à sexta das 11h às 13h.
          </MainText>
        </>
      ),
    });
  };

  const handleDinnerInfoClick = () => {
    analyticLog("click_ru_info", { meal: "dinner" });
    pushAlert({
      type: "default",
      title: "Horários do Jantar",
      isClosable: true,
      children: (
        <>
          <Box display={"flex"} alignItems={"center"} mb={1}>
            <AccountBalanceRounded />
            <MainText size="body">Unidade Central</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Segunda à sexta das 17h até às 19h.
          </MainText>
          <MainText size="caption" color="regular">
            - Sábados, domingos e feriados das 17h às 18h.
          </MainText>

          <Box display={"flex"} alignItems={"center"} mt={2} mb={1}>
            <BiotechRounded />
            <MainText size="body">Unidade Biomédica</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Segunda à sexta das 17h até às 18h45.{" "}
          </MainText>

          <MainText size="caption" color="regular">
            - Sábados, domingos e feriados das 17h às 18h.
          </MainText>

          <Box display={"flex"} alignItems={"center"} mt={2} mb={1}>
            <FunctionsRounded />
            <MainText size="body">Unidade Tecnológica</MainText>
          </Box>

          <MainText size="caption" color="regular">
            - Não tem.
          </MainText>
        </>
      ),
    });
  };

  React.useEffect(() => {
    getMenu();
  }, []);

  return (
    <>
      {loading ? (
        <>
          <Skeleton
            animation="wave"
            variant="rounded"
            width={"100%"}
            height={194}
            sx={{ borderRadius: "15px", mb: 3 }}
          />

          <Skeleton
            animation="wave"
            variant="rounded"
            width={"100%"}
            height={194}
            sx={{ borderRadius: "15px", mb: 3 }}
          />
        </>
      ) : (
        <>
          {menu?.errorGenerated && (
            <Box display={"flex"} flexDirection={"column"} gap={2} mb={2}>
              <Box
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }}
                p={2}
              >
                <MainText color="regular">⛔ Cardápio não encontrado</MainText>
                <IconButton size="small" color="inherit" onClick={handleNotFoundInfoClick}>
                  <InfoRounded fontSize={"small"} />
                </IconButton>
              </Box>

              {firstMenu !== null && (
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  gap={1}
                  p={2}
                  style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }}
                >
                  <img
                    style={{ width: "100%", maxWidth: "350px", minHeight: 60 }}
                    alt="Cardápio 1"
                    src={`https://storage.googleapis.com/circular-ufrn.appspot.com/menu%2F${todayString}.1.jpeg`}
                    onLoad={() => setFirstMenu(true)}
                    onError={() => setFirstMenu(null)}
                  />
                </Box>
              )}

              {firstMenu !== null && (
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  gap={1}
                  p={2}
                  style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }}
                >
                  <img
                    style={{ width: "100%", maxWidth: "350px", minHeight: 60 }}
                    alt="Cardápio 2"
                    src={`https://storage.googleapis.com/circular-ufrn.appspot.com/menu%2F${todayString}.2.jpeg`}
                    onLoad={() => setSecondMenu(true)}
                    onError={() => setSecondMenu(null)}
                  />
                </Box>
              )}

              <Box style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }} p={2}>
                <MainText color="regular">⛔ Qualquer coisa contate a gente acessando:</MainText>
                <Box my={1} />
                <MainText color="regular">
                  Menu {">"} Configurações {">"} Mensagem para o Dev
                </MainText>
              </Box>
            </Box>
          )}

          <Box style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }} p={2} mb={2}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
              <MainText size="subtitle">Almoço</MainText>
              <Box display={"flex"} alignItems={"center"}>
                <MainText size="small">10h30 às 13h30</MainText>
                <IconButton size="small" color="inherit" onClick={handleLunchInfoClick}>
                  <InfoRounded fontSize={"small"} />
                </IconButton>
              </Box>
            </Box>

            <Box display={"flex"} mt={2} mb={2}>
              <EggAltRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="black">
                {separateText(menu?.lunch?.protein || "")}
              </MainText>
            </Box>

            <Box display={"flex"} mb={2}>
              <DinnerDiningRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="regular">
                {separateText(menu?.lunch?.side || "")}
              </MainText>
            </Box>

            <Box display={"flex"}>
              <RiceBowlRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="regular">
                Veg: {separateText(menu?.lunch?.vegetarian || "")}
              </MainText>
            </Box>
          </Box>

          <Box style={{ borderRadius: "15px", backgroundColor: GreyPallet.Light }} p={2} mb={2}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
              <MainText size="subtitle">Jantar</MainText>
              <Box display={"flex"} alignItems={"center"}>
                <MainText size="small">17h até às 19h</MainText>
                <IconButton size="small" color="inherit" onClick={handleDinnerInfoClick}>
                  <InfoRounded fontSize={"small"} />
                </IconButton>
              </Box>
            </Box>

            <Box display={"flex"} mt={2} mb={2}>
              <EggAltRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="black">
                {separateText(menu?.dinner?.protein || "")}
              </MainText>
            </Box>

            <Box display={"flex"} mb={2}>
              <SoupKitchenRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="regular">
                {separateText(menu?.dinner?.side || "")}
              </MainText>
            </Box>

            <Box display={"flex"}>
              <RiceBowlRounded color="disabled" sx={{ mr: 1 }} />

              <MainText size="body" color="regular">
                Veg: {separateText(menu?.dinner?.vegetarian || "")}
              </MainText>
            </Box>
          </Box>
        </>
      )}

      {UtilComponents.isStaff(currentUserState) ? (
        <Box my={4}>
          <MainSolidButton
            text="Atualizar Agora"
            loading={loadingForce}
            icon={<RefreshRounded />}
            onClick={handleForceGetMenu}
          />
        </Box>
      ) : null}
    </>
  );
}
